import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { TicketStatus } from '../../types';

/** The canonical order of ticket workflow steps (admin view). */
const ADMIN_STEPS: { status: TicketStatus; label: string }[] = [
  { status: 'OPEN', label: 'Deschis' },
  { status: 'ENCORSA_ASSIGNING', label: 'Alocare' },
  { status: 'ENCORSA_ESTIMATING', label: 'Estimare' },
  { status: 'ENCORSA_IN_PROGRESS', label: 'În lucru' },
  { status: 'ENCORSA_SUPERVISOR_REVIEW', label: 'Review supervizor' },
  { status: 'RESOLVED', label: 'Rezolvat, Așteptăm Feedback' },
  { status: 'CLOSED', label: 'Închis' },
];

/** Client view: supervisor review is hidden (merged into "În lucru"). */
const CLIENT_STEPS: { status: TicketStatus; label: string }[] = [
  { status: 'OPEN', label: 'Deschis' },
  { status: 'ENCORSA_ASSIGNING', label: 'Alocare' },
  { status: 'ENCORSA_ESTIMATING', label: 'Estimare' },
  { status: 'ENCORSA_IN_PROGRESS', label: 'În lucru' },
  { status: 'RESOLVED', label: 'Rezolvat, Așteptăm Feedback' },
  { status: 'CLOSED', label: 'Închis' },
];

/** Map each status to its index in the admin flow for ordering. */
const STATUS_ORDER: Record<TicketStatus, number> = {
  OPEN: 0,
  ENCORSA_ASSIGNING: 1,
  ENCORSA_ESTIMATING: 2,
  ENCORSA_IN_PROGRESS: 3,
  ENCORSA_SUPERVISOR_REVIEW: 4,
  WAITING_CLIENT: -1, // Special: not shown as a step
  RESOLVED: 5,
  CLOSED: 6,
};

interface TicketProgressProps {
  currentStatus: TicketStatus;
  statusHistory?: { fromStatus?: TicketStatus | null; toStatus: TicketStatus }[];
  view: 'admin' | 'client';
}

export function TicketProgress({ currentStatus, statusHistory, view }: TicketProgressProps) {
  const steps = view === 'admin' ? ADMIN_STEPS : CLIENT_STEPS;

  // Build a set of statuses that have been visited (from history)
  const visited = new Set<TicketStatus>();
  if (statusHistory) {
    for (const h of statusHistory) {
      visited.add(h.toStatus);
      if (h.fromStatus) visited.add(h.fromStatus);
    }
  }
  // Current status is always visited
  visited.add(currentStatus);

  // For client view, if supervisor review was visited, also mark IN_PROGRESS as visited
  if (view === 'client' && visited.has('ENCORSA_SUPERVISOR_REVIEW')) {
    visited.add('ENCORSA_IN_PROGRESS');
  }

  // Determine where the current status falls in the step list
  const currentOrder = STATUS_ORDER[currentStatus];
  // For client: SUPERVISOR_REVIEW maps to IN_PROGRESS position
  const effectiveCurrentStatus = (view === 'client' && currentStatus === 'ENCORSA_SUPERVISOR_REVIEW')
    ? 'ENCORSA_IN_PROGRESS'
    : currentStatus;

  // WAITING_CLIENT is a side-branch: show a subtle indicator but don't break the main flow
  const isWaitingClient = currentStatus === 'WAITING_CLIENT';

  return (
    <div className="w-full">
      {isWaitingClient && (
        <div className="mb-2 text-center">
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-medium text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
            ⏳ {view === 'client' ? 'La mine pt. clarificări' : 'La client pt. clarificări'}
          </span>
        </div>
      )}
      <div className="flex items-center">
        {steps.map((step, i) => {
          const stepOrder = STATUS_ORDER[step.status];
          const isCurrent = step.status === effectiveCurrentStatus && !isWaitingClient;
          const isPast = visited.has(step.status) && stepOrder < (isWaitingClient ? currentOrder : STATUS_ORDER[effectiveCurrentStatus]);
          const isCompleted = isPast || (visited.has(step.status) && stepOrder < currentOrder);
          const isFuture = !isCurrent && !isCompleted;

          return (
            <div key={step.status} className="flex flex-1 items-center">
              <div className="flex flex-col items-center flex-1">
                {/* Circle */}
                <div
                  className={cn(
                    'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold border-2 transition-colors',
                    isCurrent && 'border-primary bg-primary text-white',
                    isCompleted && 'border-green-500 bg-green-500 text-white',
                    isFuture && 'border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-dark-card dark:text-gray-500',
                  )}
                >
                  {isCompleted ? <Check size={14} /> : i + 1}
                </div>
                {/* Label */}
                <span
                  className={cn(
                    'mt-1 text-[10px] font-medium text-center leading-tight max-w-[80px]',
                    isCurrent && 'text-primary dark:text-primary',
                    isCompleted && 'text-green-600 dark:text-green-400',
                    isFuture && 'text-gray-400 dark:text-gray-500',
                  )}
                >
                  {step.label}
                </span>
              </div>
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div
                  className={cn(
                    'h-0.5 flex-1 -mt-4',
                    stepOrder < (isWaitingClient ? currentOrder : STATUS_ORDER[effectiveCurrentStatus])
                      ? 'bg-green-500'
                      : 'bg-gray-300 dark:bg-gray-600',
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
